/**
 * De menubalk van de groepssite (www.hilfertheem.nl) boven de publieke
 * pagina's, zodat de Heldensite er één geheel mee vormt.
 *
 * Nagebouwd, geen iframe: in een iframe kan Mollie niet betalen, en de links
 * uit de mails openen de Heldensite toch los. Verandert het menu op de
 * groepssite, dan moet het hier mee (`MENU`): een commit en een uitrol, net
 * als de teksten. Maten en kleuren komen uit het WordPress-thema van de
 * groep: 80 px hoog, Poppins 300 in groen, bruine streep bij hover en bij
 * het actieve item, het logo van Scouting op de stempel die over de rand hangt.
 * Onder 1100 px een hamburger, met de submenu's uitgeklapt eronder:
 * smaller past het menu niet naast het logo. Vast bovenaan alleen vanaf
 * 1025 px, zoals het thema (`#header.menu-top` is daaronder `relative`): op
 * telefoon en tablet scrolt de balk mee weg.
 */

import { useEffect, useId, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const GROEP = 'https://www.hilfertheem.nl';

type Menuitem = { label: string; href: string; sub?: { label: string; href: string }[]; hier?: boolean };

const MENU: Menuitem[] = [
  { label: 'Wie zijn wij', href: `${GROEP}/wie-zijn-wij/` },
  {
    label: 'Speltakken',
    href: `${GROEP}/speltakken/`,
    sub: [
      { label: 'Bevers (5-7 jaar)', href: `${GROEP}/bevers/` },
      { label: 'Welpen (7-11 jaar)', href: `${GROEP}/welpen/` },
      { label: 'Verkenners (11-14 jaar)', href: `${GROEP}/verkenners/` },
      { label: 'Rowans (15-18 jaar)', href: `${GROEP}/rowans/` },
      { label: 'Stam (18+)', href: `${GROEP}/stam/` },
    ],
  },
  { label: 'Verhuur clubhuis', href: `${GROEP}/verhuur-clubhuis-hilversum/` },
  {
    label: 'Geschiedenis',
    href: `${GROEP}/geschiedenis/`,
    sub: [
      { label: 'Gooische Treckers', href: `${GROEP}/geschiedenis/gooische-treckers/` },
      { label: 'De oude Hilfertheem', href: `${GROEP}/geschiedenis/de-oude-hilfertheem/` },
      { label: 'Naerdinclant', href: `${GROEP}/geschiedenis/naerdinclant/` },
    ],
  },
  { label: 'Lid worden', href: `${GROEP}/lid-worden/` },
  { label: 'Fotoboek', href: 'https://fotoboek.hilfertheem.nl' },
  { label: 'HH Helden', href: '/', hier: true },
  { label: 'Contact', href: `${GROEP}/contact/` },
];

const MAIL = 'info@hilfertheem.nl';

export function Groepsmenu() {
  const [open, zetOpen] = useState(false);
  const paneel = useId();
  const { pathname } = useLocation();

  useEffect(() => zetOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    const toets = (e: KeyboardEvent) => e.key === 'Escape' && zetOpen(false);
    window.addEventListener('keydown', toets);
    return () => window.removeEventListener('keydown', toets);
  }, [open]);

  return (
    <div className="groepsmenu relative z-40 h-20 bg-white min-[1025px]:sticky min-[1025px]:top-0">
      <div aria-hidden="true" className="groepsmenu-stempel" />
      <a href={GROEP} className="groepsmenu-logo" title="Scouting Hilfertheem">
        <img src="/groepsmenu-logo.svg" alt="Scouting Hilfertheem" width={120} height={105} />
      </a>

      <nav aria-label="Scouting Hilfertheem" className="flex h-full items-center justify-end px-4">
        <ul className="hidden items-center min-[1100px]:flex">
          {MENU.map((item) => (
            <li key={item.label} className="group relative">
              <Menulink item={item} className="groepsmenu-link" />
              {item.sub && (
                <ul className="groepsmenu-sub">
                  {item.sub.map((s) => (
                    <li key={s.label}>
                      <a href={s.href}>{s.label}</a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
          <li>
            <a href={`mailto:${MAIL}`} className="groepsmenu-mail" title="Stuur een e-mail">
              <Envelop />
              <span className="sr-only">Stuur een e-mail</span>
            </a>
          </li>
        </ul>

        <button
          type="button"
          className="groepsmenu-hamburger min-[1100px]:hidden"
          aria-expanded={open}
          aria-controls={paneel}
          onClick={() => zetOpen((o) => !o)}
        >
          <svg viewBox="0 0 28 24" width="28" height="24" aria-hidden="true" fill="currentColor">
            <rect y="1" width="28" height="4" rx="1" />
            <rect y="10" width="28" height="4" rx="1" />
            <rect y="19" width="28" height="4" rx="1" />
          </svg>
          <span className="sr-only">{open ? 'Menu sluiten' : 'Menu'}</span>
        </button>
      </nav>

      <div id={paneel} hidden={!open} className="groepsmenu-paneel min-[1100px]:hidden">
        <ul>
          {MENU.map((item) => (
            <li key={item.label}>
              <Menulink item={item} className="groepsmenu-paneel-link" />
              {item.sub && (
                <ul className="pb-2 pl-4">
                  {item.sub.map((s) => (
                    <li key={s.label}>
                      <a href={s.href} className="groepsmenu-paneel-sub">
                        {s.label}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
          <li>
            <a href={`mailto:${MAIL}`} className="groepsmenu-paneel-link inline-flex items-center gap-2">
              <Envelop /> Stuur een e-mail
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
}

function Menulink({ item, className }: { item: Menuitem; className: string }) {
  if (item.hier) {
    return (
      <Link to={item.href} className={className} aria-current="page" data-hier="">
        {item.label}
      </Link>
    );
  }
  return (
    <a href={item.href} className={className}>
      {item.label}
    </a>
  );
}

function Envelop() {
  return (
    <svg viewBox="0 0 24 18" width="24" height="18" aria-hidden="true" fill="currentColor">
      <path d="M2.2 0h19.6A2.2 2.2 0 0 1 24 2.2v.5l-12 7.6L0 2.7v-.5A2.2 2.2 0 0 1 2.2 0ZM0 5.1l12 7.6 12-7.6v10.7a2.2 2.2 0 0 1-2.2 2.2H2.2A2.2 2.2 0 0 1 0 15.8Z" />
    </svg>
  );
}
